import type { PropsWithChildren } from "react";
import type React from "react";
import cn from "classnames";
import { clamp, isNil } from "lodash";
import styles from "./Grid.module.scss";

/** Valid column span values for the grid system */
export type GridSpan = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;

interface FieldProps {
    spanMobile?: GridSpan;
    spanTablet?: GridSpan;
    spanDesktop?: GridSpan;
    className?: string;
}

interface ContainerProps {
    className?: string;
}

interface IGrid {
    Container: React.FC<PropsWithChildren<ContainerProps>>;
    Field: React.FC<PropsWithChildren<FieldProps>>;
}

// Make TS happy with this typeguard, to check whether className may be an index of styles.
// We expect className param to always be a string but typescript may expect numbers to be
// a valid keys in `styles` dict, so we add number as part union
const isInClassNames = (className: string | number): className is keyof typeof styles => className in styles;

// Construct className on given input and return valid mapped css module className
const getFieldClassName = (clampedSpan: number | null, prefix: "colXs" | "colMd" | "col") => {
    if (!isNil(clamp)) {
        const className = `${prefix}${clampedSpan}`;
        if (isInClassNames(className)) return styles[className];
    }
    return "";
};

/**
 * A responsive grid system component that provides a 12-column layout
 * with support for different column spans on mobile and desktop devices.
 *
 * @example
 * ```tsx
 * <Grid.Container>
 *   <Grid.Field spanMobile={12} spanDesktop={6}>
 *     Content
 *   </Grid.Field>
 * </Grid.Container>
 * ```
 */
export const Grid: IGrid = {
    /**
     * Container component that creates a 12-column grid layout
     *
     * @param {PropsWithChildren<ContainerProps>} props - Component props
     * @param {ReactNode} props.children - Child elements to render within the container
     * @param {string} [props.className] - Additional CSS classes
     * @returns {JSX.Element} Grid container element
     */
    Container: ({ children, className }) => {
        const mergedClassname = cn(styles.container, className);
        return <div className={mergedClassname}>{children}</div>;
    },
    /**
     * Field component that defines how many columns an element should span
     *
     * @param {PropsWithChildren<FieldProps>} props - Component props
     * @param {ReactNode} props.children - Child elements to render within the field
     * @param {GridSpan} [props.spanMobile] - Number of columns to span on mobile
     * @param {GridSpan} [props.spanDesktop] - Number of columns to span on desktop
     * @param {string} [props.className] - Additional CSS classes
     * @returns {JSX.Element} Grid field element
     */
    Field: ({ children, spanMobile, spanTablet, spanDesktop, className }) => {
        const validSpanMobile = !isNil(spanMobile) ? clamp(spanMobile, 1, 12) : null;
        const validSpanTablet = !isNil(spanTablet) ? clamp(spanTablet, 1, 12) : null;
        const validSpanDesktop = !isNil(spanDesktop) ? clamp(spanDesktop, 1, 12) : null;

        const mergedClassname = cn(
            styles.col,
            getFieldClassName(validSpanMobile, "colXs"),
            getFieldClassName(validSpanTablet, "colMd"),
            getFieldClassName(validSpanDesktop, "col"),
            className,
        );

        return <div className={mergedClassname}>{children}</div>;
    },
};
